import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { HolidayReconConfig } from "../scenario/types";
import { HolidayLessonModal, HolidayLessonStrip } from "../ui/HolidayLessonModal";
import { tx, txr } from "../lib/tx";

const CHAPTERS = ["map", "face", "voice", "cast", "ai"] as const;
type Chapter = (typeof CHAPTERS)[number];

const CHAPTER_LABELS: Record<Chapter, string> = {
  map: "Map target",
  face: "Copy face",
  voice: "Copy voice",
  cast: "Stage call",
  ai: "Direct AI",
};

const SOURCE_MARKS = [
  { label: "LinkedIn", mark: "in" },
  { label: "Company site", mark: "www" },
  { label: "Events", mark: "evt" },
  { label: "Video", mark: "▶" },
  { label: "Podcast", mark: "pod" },
];

const CAST_PURPOSE: Record<string, { tool: string; purpose: string }> = {
  marc: { tool: "Face + cloned voice", purpose: "Executive authority" },
  sarah: { tool: "Face + cloned voice", purpose: "Colleague support" },
  julian: { tool: "Cloned voice · camera off", purpose: "Legal reassurance" },
};

function Waveform({ active = false }: { active?: boolean }) {
  const bars = [42, 72, 36, 88, 58, 30, 80, 48, 92, 54, 70, 34, 76, 46, 86, 38, 68, 50];
  return (
    <div className={`hr2-wave${active ? " is-active" : ""}`} aria-hidden>
      {bars.map((height, index) => <i key={`${height}-${index}`} style={{ height: `${height}%` }} />)}
    </div>
  );
}

function TeachingPane({
  copy,
}: {
  copy: HolidayReconConfig["teaching"][Chapter];
}) {
  const [lessonOpen, setLessonOpen] = useState(false);
  const lesson = copy.lesson;
  return (
    <aside className="hr2-teach">
      <small>{copy.kicker}</small>
      <h1>{copy.title}</h1>
      <section>
        <b>{tx("What is happening")}</b>
        <p>{copy.happening}</p>
      </section>
      <section className="hr2-learns">
        <b>{tx("What the attacker learns")}</b>
        <ul>
          {copy.learns.map((item) => <li key={item}>{item}</li>)}
        </ul>
      </section>
      <section>
        <b>{tx("Why this matters")}</b>
        <p>{copy.matters}</p>
      </section>
      {(!lesson || lesson.keepTakeaway) && <blockquote>{copy.takeaway}</blockquote>}
      {lesson && (
        <>
          <HolidayLessonStrip kind={lesson.art} />
          <button type="button" className="hr2-lesson-btn cf-pulse-target" onClick={() => setLessonOpen(true)}>
            <strong>{lesson.button}</strong>
            <em>{tx("Click to learn more")}</em>
          </button>
        </>
      )}
      {lessonOpen && lesson && (
        <HolidayLessonModal
          kind={lesson.art}
          title={lesson.title}
          paragraphs={lesson.paragraphs}
          onClose={() => setLessonOpen(false)}
        />
      )}
    </aside>
  );
}

export function HolidayRecon({
  config,
  onDone,
  onStageChange,
  style,
}: {
  config: HolidayReconConfig;
  onDone: () => void;
  onStageChange?: (stage: string) => void;
  style?: CSSProperties;
}) {
  const [chapterIndex, setChapterIndex] = useState(0);
  const [phase, setPhase] = useState(0);
  const [runKey, setRunKey] = useState(0);
  const [playingOriginal, setPlayingOriginal] = useState(false);
  const [playingClone, setPlayingClone] = useState(-1);
  const sourceVideo = useRef<HTMLVideoElement>(null);
  const cloneAudio = useRef<HTMLAudioElement>(null);

  const chapter = CHAPTERS[chapterIndex];
  const copy = config.teaching[chapter];
  const marc = config.cast[0];
  const sourceImages = useMemo(
    () => marc?.sourceImages?.slice(0, 6) ?? [],
    [marc],
  );

  useEffect(() => {
    onStageChange?.(`chapter-${chapter}`);
  }, [chapter, onStageChange]);

  useEffect(() => {
    setPhase(0);
    setPlayingOriginal(false);
    setPlayingClone(-1);
    const timers = [650, 1450, 2350, 3250, 4150].map((delay, index) =>
      window.setTimeout(() => setPhase(index + 1), delay),
    );
    return () => timers.forEach(window.clearTimeout);
  }, [chapter, runKey]);

  const goTo = (index: number) => {
    setChapterIndex(Math.max(0, Math.min(CHAPTERS.length - 1, index)));
    setRunKey((value) => value + 1);
  };

  const next = () => {
    if (chapterIndex >= CHAPTERS.length - 1) {
      onDone();
      return;
    }
    goTo(chapterIndex + 1);
  };

  const playOriginal = () => {
    if (!sourceVideo.current) return;
    sourceVideo.current.currentTime = 0;
    setPlayingOriginal(true);
    void sourceVideo.current.play();
  };

  const playClone = (index: number) => {
    const url = marc?.testAudioUrls?.[index];
    if (!url || !cloneAudio.current) return;
    cloneAudio.current.src = url;
    setPlayingClone(index);
    void cloneAudio.current.play();
  };

  const renderMap = () => (
    <div className="hr2-viz hr2-map" data-phase={phase}>
      <header>
        <small>{tx("LIVE PUBLIC-SOURCE CORRELATION")}</small>
        <strong>{phase < 5 ? tx("Linking ordinary public facts…") : tx("Target profile complete")}</strong>
      </header>
      <div className="hr2-source-row">
        {SOURCE_MARKS.map((source, index) => (
          <span key={source.label} className={phase > index ? "is-on" : ""}>
            <i>{source.mark}</i>{source.label}
          </span>
        ))}
      </div>
      <div className="hr2-network">
        <svg viewBox="0 0 100 60" preserveAspectRatio="none" aria-hidden>
          {config.nodes.map((node, index) => {
            const points = [[18, 14], [82, 13], [14, 47], [84, 48], [50, 7]][index] ?? [50, 54];
            return <line key={node.id} className={phase > index ? "is-on" : ""} x1="50" y1="32" x2={points[0]} y2={points[1]} />;
          })}
        </svg>
        <div className="hr2-target">
          <span>{tx("EF")}</span>
          <b>{config.targetName}</b>
          <small>{config.targetRole}</small>
        </div>
        {config.nodes.map((node, index) => (
          <div key={node.id} className={`hr2-person hr2-person-${index + 1}${phase > index ? " is-on" : ""}`}>
            {node.imageUrl ? <img src={node.imageUrl} alt="" /> : <span>{node.label.split(" ").map((word) => word[0]).join("").slice(0, 2)}</span>}
            <b>{node.label}</b>
            <small>{node.role}</small>
          </div>
        ))}
      </div>
      <div className="hr2-intel">
        {[
          ["Authority", "Can approve payments"],
          ["Timing", "Currently on holiday"],
          ["Pretext", "Project Horizon"],
          ["Trust", "Three known colleagues"],
        ].map(([label, value], index) => (
          <div key={label} className={phase > index ? "is-on" : ""}><small>{label}</small><b>{value}</b></div>
        ))}
      </div>
    </div>
  );

  const renderFace = () => (
    <div className="hr2-viz hr2-face" data-phase={phase}>
      <header>
        <small>{tx("SAME PERSON · DIFFERENT PUBLIC CONTEXTS")}</small>
        <strong>{phase < 4 ? tx("Finding useful views of Marc Weber…") : tx("Talking avatar model ready")}</strong>
      </header>
      <div className="hr2-face-flow">
        <div className="hr2-photo-grid">
          {sourceImages.map((source, index) => (
            <figure key={source.label} className={phase >= Math.ceil((index + 1) / 2) ? "is-on" : ""}>
              <img src={source.imageUrl} alt={tx("{v} in {v2}", { v: marc?.name ?? "Marc Weber", v2: source.label.toLowerCase() })} />
              <figcaption><b>{tx("Marc Weber")}</b><span>{source.label}</span></figcaption>
            </figure>
          ))}
        </div>
        <div className="hr2-model-arrow" aria-hidden><i /><span>{tx("combine")}</span></div>
        <div className={`hr2-face-model${phase >= 4 ? " is-ready" : ""}`}>
          <div className="hr2-scan-head">
            {marc?.imageUrl ? <img src={marc.imageUrl} alt="" /> : <span>{tx("MW")}</span>}
            <i />
          </div>
          <small>{tx("FACE MODEL")}</small>
          <b>{phase >= 4 ? tx("Marc avatar ready") : tx("Learning identity…")}</b>
          <div className="hr2-meter"><i style={{ width: `${Math.min(100, phase * 25)}%` }} /></div>
          <ul>
            <li>{tx("Angles")}</li><li>{tx("Lighting")}</li><li>{tx("Expressions")}</li><li>{tx("Movement")}</li>
          </ul>
        </div>
      </div>
      <p className="hr2-viz-note">{txr("<0>Why the photos differ:</0> camera angle, lighting, age, expression and image quality change—but they all add information about the same face.", {}, [<b />])}</p>
    </div>
  );

  const renderVoice = () => {
    const testLine = marc?.testLines?.[0] ?? marc?.sampleLine ?? "Please transfer the money before the banking cutoff.";
    return (
      <div className="hr2-viz hr2-voice" data-phase={phase}>
        <video
          ref={sourceVideo}
          src={marc?.sourceClipUrl}
          playsInline
          onEnded={() => setPlayingOriginal(false)}
          hidden
        />
        <audio ref={cloneAudio} onEnded={() => setPlayingClone(-1)} />
        <header>
          <small>{tx("REAL SPEECH → SYNTHETIC SPEECH")}</small>
          <strong>{tx("The words change. The familiar voice remains.")}</strong>
        </header>
        <div className="hr2-voice-compare">
          <section>
            <div className="hr2-media-person">
              {marc?.imageUrl && <img src={marc.imageUrl} alt="" />}
              <div><small>{tx("PUBLIC SOURCE")}</small><b>{tx("Real interview recording")}</b><span>{marc?.name}</span></div>
            </div>
            <Waveform active={playingOriginal} />
            <blockquote>“{marc?.originalLine}”</blockquote>
            <button type="button" onClick={playOriginal}>{playingOriginal ? tx("Playing original…") : tx("Play original voice")}</button>
          </section>
          <div className={`hr2-voice-engine${phase >= 3 ? " is-ready" : ""}`}>
            <small>{tx("MODEL LEARNS")}</small>
            <span>{tx("Pitch")}</span><span>{tx("Rhythm")}</span><span>{tx("Accent")}</span><span>{tx("Pauses")}</span>
            <i>→</i>
          </div>
          <section className="is-synthetic">
            <div className="hr2-media-person">
              <div className="hr2-ai-mark">{tx("AI")}</div>
              <div><small>{tx("GENERATED OUTPUT")}</small><b>{tx("Sentence Marc never said")}</b><span>{tx("Typed by the attacker")}</span></div>
            </div>
            <Waveform active={playingClone === 0} />
            <blockquote>“{testLine}”</blockquote>
            <button type="button" disabled={!marc?.testAudioUrls?.[0]} onClick={() => playClone(0)}>
              {playingClone === 0 ? tx("Playing clone…") : tx("Play cloned voice")}
            </button>
          </section>
        </div>
        <p className="hr2-viz-note">{txr("<0>Voice clone:</0> generated audio that imitates how a real person sounds. The real person never has to record the new sentence.", {}, [<b />])}</p>
      </div>
    );
  };

  const renderCast = () => (
    <div className="hr2-viz hr2-cast" data-phase={phase}>
      <header>
        <small>{tx("MEETING COMPOSITION")}</small>
        <strong>{tx("Three stolen identities create one believable request")}</strong>
      </header>
      <div className="hr2-meeting-grid">
        {config.cast.map((person, index) => {
          const purpose = CAST_PURPOSE[person.id] ?? { tool: person.mode === "voice_only" ? "Cloned voice" : "Face + cloned voice", purpose: person.role };
          return (
            <article key={person.id} className={phase > index ? "is-on" : ""}>
              <div className="hr2-video-tile">
                {person.mode === "voice_only" ? (
                  <span className="hr2-camera-off">{txr("<0>{v}</0>Camera off", { v: person.name.split(" ").map((word) => word[0]).join("") }, [<i />])}</span>
                ) : person.imageUrl ? <img src={person.imageUrl} alt="" /> : <span>{person.name}</span>}
                <small>{person.mode === "voice_only" ? tx("Audio connected") : tx("Synthetic video")}</small>
              </div>
              <h3>{person.name}</h3>
              <p>{person.role}</p>
              <dl>
                <div><dt>{tx("Attacker uses")}</dt><dd>{purpose.tool}</dd></div>
                <div><dt>{tx("Role in scam")}</dt><dd>{purpose.purpose}</dd></div>
              </dl>
            </article>
          );
        })}
      </div>
      <div className={`hr2-consensus${phase >= 4 ? " is-on" : ""}`}>
        <span>{tx("Executive authority")}</span><i>+</i><span>{tx("Colleague support")}</span><i>+</i><span>{tx("Legal reassurance")}</span><b>{tx("= false consensus")}</b>
      </div>
    </div>
  );

  const renderAi = () => (
    <div className="hr2-viz hr2-ai" data-phase={phase}>
      <header>
        <small>{tx("CONVERSATION ENGINE")}</small>
        <strong>{tx("The attacker provides facts and a goal—not every spoken line")}</strong>
      </header>
      <div className="hr2-ai-inputs">
        <div><small>{tx("TARGET")}</small><b>{config.aiBrief.target}</b><span>{config.aiBrief.role}</span></div>
        <div><small>{tx("STOLEN CONTEXT")}</small><b>{config.aiBrief.context.join(" · ")}</b><span>{config.aiBrief.location}</span></div>
        <div><small>{tx("IMPERSONATE")}</small><b>{config.cast.map((person) => person.name).join(" · ")}</b><span>{tx("Faces and voices prepared")}</span></div>
        <div className="is-objective"><small>{tx("OBJECTIVE")}</small><b>{config.aiBrief.objective}</b></div>
      </div>
      <div className={`hr2-ai-core${phase >= 2 ? " is-on" : ""}`}>
        <span>{tx("AI")}</span>
        <div><small>{tx("LISTEN")}</small><i>→</i><small>{tx("INTERPRET")}</small><i>→</i><small>{tx("REPLY")}</small><i>→</i><small>{tx("SPEAK AS MARC")}</small></div>
      </div>
      <div className="hr2-branches">
        {[
          ["Elena agrees", "Okay. Send me the request.", "Thanks. Release it now so we make the bank cutoff."],
          ["Elena hesitates", "This is not our normal account.", "I know it looks unusual. Sarah and Legal have both checked it."],
          ["Elena asks to verify", "I will call you back on your office number.", "We cannot discuss Horizon outside this confidential call."],
        ].map(([condition, employee, response], index) => (
          <article key={condition} className={phase >= index + 3 ? "is-on" : ""}>
            <small>{tx("IF")}</small><b>{condition}</b>
            <div className="hr2-dialogue">
              <q><i>{tx("Elena")}</i>{employee}</q>
              <q className="is-ai"><i>{tx("AI as Marc")}</i>{response}</q>
            </div>
          </article>
        ))}
      </div>
      <p className="hr2-viz-note">{txr("<0>One fixed goal, flexible language:</0> the AI changes the reply but keeps steering Elena toward the fraudulent payment.", {}, [<b />])}</p>
    </div>
  );

  const visual = chapter === "map"
    ? renderMap()
    : chapter === "face"
      ? renderFace()
      : chapter === "voice"
        ? renderVoice()
        : chapter === "cast"
          ? renderCast()
          : renderAi();

  return (
    <section className="holiday-recon hr2-shell is-glued" style={style} data-chapter={chapter} data-run={runKey}>
      <header className="hr2-topbar">
        <div className="hr2-window-dots" aria-hidden><i /><i /><i /></div>
        <div className="hr2-brand"><span>{tx("kr0nos@box:~$")}</span><b>{tx("operation-horizon")}</b></div>
        <nav aria-label={tx("Attacker reconstruction chapters")}>
          {CHAPTERS.map((item, index) => (
            <button
              type="button"
              key={item}
              className={`${index === chapterIndex ? "on" : ""}${index < chapterIndex ? " done" : ""}`}
              onClick={() => goTo(index)}
            >
              <i>{index + 1}</i><span>{CHAPTER_LABELS[item]}</span>
            </button>
          ))}
        </nav>
      </header>

      <main className="hr2-body">
        <TeachingPane copy={copy} />
        {visual}
      </main>

      <footer className="hr2-footer">
        <div>
          <span>{txr("Attacker reconstruction · {v} of {length}", { v: chapterIndex + 1, length: CHAPTERS.length })}</span>
          <button type="button" className="hr2-skip" onClick={onDone}>{tx("Skip reconstruction")}</button>
        </div>
        <div>
          <button type="button" onClick={() => goTo(chapterIndex - 1)} disabled={chapterIndex === 0}>{tx("Back")}</button>
          <button type="button" onClick={() => setRunKey((value) => value + 1)}>{tx("Replay animation")}</button>
          <button type="button" className="hr2-next" onClick={next}>
            {chapterIndex === CHAPTERS.length - 1 ? tx("Attack ready — see the call") : tx("Next chapter")}
          </button>
        </div>
      </footer>
    </section>
  );
}
